import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import ConfirmModal from '../components/ConfirmModal';
import { getTenants, createTenant, updateTenant, deleteTenant, Tenant, uploadFile } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import PlaceSearch from '../components/PlaceSearch';
import GoogleMap from '../components/GoogleMap';
import TenantAdminLoginsPanel from '../components/TenantAdminLoginsPanel';
import { formatDateDDMMYYYY } from '../utils/dateUtils';

const TenantManagement: React.FC = () => {
  const { hasPermission, isSystemAdmin } = useAuth();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<{ id: string; name: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    domain: '',
    email: '',
    phone: '',
    password: '',
    address: '',
    description: '',
    status: 'active',
    groupId: '',
    hospital: {
      name: '',
      logo: '',
      addressDetails: '',
      addressFormatted: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
      phone: '',
      email: '',
      website: '',
      registrationNumber: '',
      licenseNumber: '',
      emergencyContact: '',
      emergencyPhone: '',
      latitude: undefined as number | undefined,
      longitude: undefined as number | undefined,
    },
  });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    name: '',
    code: '',
    email: '',
    phone: '',
    password: '',
    status: '',
    hospitalName: '',
    hospitalLocation: '',
    hospitalEmail: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [hospitalLocation, setHospitalLocation] = useState<{ latitude: number; longitude: number; address?: string } | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({ lat: 28.6139, lng: 77.209 });
  const [adminLoginsTenant, setAdminLoginsTenant] = useState<{ id: string; name: string } | null>(null);

  const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setLogoUploadError('Please select an image file');
      return;
    }
    const maxBytes = 2 * 1024 * 1024;
    if (file.size > maxBytes) {
      const { formatFileSizeMB } = await import('../utils/imageCompression');
      setLogoUploadError(
        `The selected image (${formatFileSizeMB(file.size)}) exceeds the maximum allowed size (2 MB). Please choose a smaller file.`
      );
      return;
    }

    setLogoUploadError('');
    setLogoUploading(true);
    const run = async () => {
      try {
        const { compressImage } = await import('../utils/imageCompression');
        const compressedFile = await compressImage(file, 800, 800, 0.8, 300);

        const form = new FormData();
        form.append('file', compressedFile);
        form.append('folderPath', 'images/hospital-logos');
        const response = await uploadFile(form);
        const url = response.data?.data?.url;
        if (url) {
          setFormData((prev) => ({
            ...prev,
            hospital: { ...prev.hospital, logo: url },
          }));
        } else {
          setLogoUploadError('Upload succeeded but no URL returned.');
        }
      } catch (err: any) {
        setLogoUploadError(err.response?.data?.message || err.message || 'Failed to upload logo');
      } finally {
        setLogoUploading(false);
        if (e.target) e.target.value = '';
      }
    };
    setTimeout(run, 0);
  };

  // Close drawer with Escape key
  useEffect(() => {
    if (!showModal) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        const activeElement = document.activeElement as HTMLElement | null;
        if (activeElement && typeof activeElement.blur === 'function') {
          activeElement.blur();
        }
        handleCloseModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showModal]);

  const handleClearLogo = () => {
    setFormData((prev) => ({
      ...prev,
      hospital: { ...prev.hospital, logo: '' },
    }));
    setLogoUploadError('');
  };

  // Debounce search term - wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 800);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  const fetchTenants = useCallback(async (pageOverride?: number, pageSizeOverride?: number) => {
    try {
      setLoading(true);
      const pageToUse = pageOverride ?? currentPage;
      const sizeToUse = pageSizeOverride ?? pageSize;
      const response = await getTenants(pageToUse, sizeToUse, debouncedSearchTerm || undefined);
      setTenants(response.data.data);
      const pagination = response.data.pagination;
      if (pagination) {
        setCurrentPage(pagination.currentPage || pageToUse);
        setPageSize(pagination.pageSize || sizeToUse);
        setTotalItems(pagination.totalItems || 0);
        setHasNextPage(pagination.hasNextPage || false);
        setHasPreviousPage(pagination.hasPreviousPage || false);
      }
    } catch (err: any) {
      setError('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearchTerm]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const handleOpenModal = (tenant?: Tenant) => {
    if (tenant) {
      setEditingTenant(tenant);
      setFormData({
        name: tenant.metadata.name,
        code: tenant.metadata.code || '',
        domain: tenant.spec.domain || '',
        email: tenant.spec.email || '',
        phone: tenant.spec.phone || '',
        password: '',
        address: tenant.spec.address || '',
        description: tenant.spec.description || '',
        status: tenant.spec.status,
        groupId: tenant.spec.groupId || '',
        hospital: {
          name: tenant.spec.hospital?.name || '',
          logo: tenant.spec.hospital?.logo || '',
          addressDetails: '',
          addressFormatted: tenant.spec.hospital?.address || '',
          city: tenant.spec.hospital?.city || '',
          state: tenant.spec.hospital?.state || '',
          zipCode: tenant.spec.hospital?.zipCode || '',
          country: tenant.spec.hospital?.country || '',
          phone: tenant.spec.hospital?.phone || '',
          email: tenant.spec.hospital?.email || '',
          website: tenant.spec.hospital?.website || '',
          registrationNumber: tenant.spec.hospital?.registrationNumber || '',
          licenseNumber: tenant.spec.hospital?.licenseNumber || '',
          emergencyContact: tenant.spec.hospital?.emergencyContact || '',
          emergencyPhone: tenant.spec.hospital?.emergencyPhone || '',
          latitude: tenant.spec.hospital?.latitude,
          longitude: tenant.spec.hospital?.longitude,
        },
      });
      if (tenant.spec.hospital?.latitude !== undefined && tenant.spec.hospital?.longitude !== undefined) {
        setHospitalLocation({
          latitude: tenant.spec.hospital.latitude,
          longitude: tenant.spec.hospital.longitude,
          address: tenant.spec.hospital.address,
        });
        setMapCenter({ lat: tenant.spec.hospital.latitude, lng: tenant.spec.hospital.longitude });
      } else {
        setHospitalLocation(null);
        setMapCenter({ lat: 28.6139, lng: 77.209 });
      }
    } else {
      setEditingTenant(null);
      setFormData({
        name: '',
        code: '',
        domain: '',
        email: '',
        phone: '',
        password: '',
        address: '',
        description: '',
        status: 'active',
        groupId: '',
        hospital: {
          name: '',
          logo: '',
          addressDetails: '',
          addressFormatted: '',
          city: '',
          state: '',
          zipCode: '',
          country: '',
          phone: '',
          email: '',
          website: '',
          registrationNumber: '',
          licenseNumber: '',
          emergencyContact: '',
          emergencyPhone: '',
          latitude: undefined,
          longitude: undefined,
        },
      });
      setHospitalLocation(null);
      setMapCenter({ lat: 28.6139, lng: 77.209 });
    }
    setShowModal(true);
    setError('');
    setShowPassword(false);
    setValidationErrors({
      name: '',
      code: '',
      email: '',
      phone: '',
      password: '',
      status: '',
      hospitalName: '',
      hospitalLocation: '',
      hospitalEmail: '',
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTenant(null);
    setError('');
    setShowPassword(false);
    setValidationErrors({
      name: '',
      code: '',
      email: '',
      phone: '',
      password: '',
      status: '',
      hospitalName: '',
      hospitalLocation: '',
      hospitalEmail: '',
    });
  };

  const validateName = (name: string): string => {
    if (!name.trim()) return 'Tenant name is required';
    return '';
  };

  const validateCode = (code: string): string => {
    if (!code.trim()) return 'Tenant code is required';
    // Only allow uppercase letters and numbers
    const codeRegex = /^[A-Z0-9]+$/;
    if (!codeRegex.test(code.trim())) {
      return 'Tenant code must contain only uppercase letters and numbers';
    }
    const codeUpper = code.trim().toUpperCase();
    if (codeUpper === 'AMS' || codeUpper === 'AMBULANCE') {
      return `Tenant code "${codeUpper}" is reserved and cannot be used`;
    }
    return '';
  };

  const validateEmail = (email: string): string => {
    if (!email) return '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Invalid email format';
    }
    return '';
  };

  const validatePhone = (phone: string): string => {
    if (!phone) return '';
    // Remove spaces, dashes, and parentheses for validation
    const cleanedPhone = phone.replace(/[\s\-()]/g, '');
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(cleanedPhone)) {
      return 'Phone number must be exactly 10 digits';
    }
    return '';
  };

  const validatePassword = (password: string): string => {
    if (!password) return editingTenant ? '' : 'Password is required';
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    return '';
  };

  // Capitalize the first letter of each word in a name
  const capitalizeName = (value: string): string => {
    if (!value || typeof value !== 'string') return value;
    return value
      .trim()
      .split(/\s+/)
      .map((word) => {
        if (!word) return word;
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  };

  const reverseGeocode = async (
    lat: number,
    lng: number
  ): Promise<{ formattedAddress: string | null; city?: string; state?: string; zip?: string; country?: string }> => {
    if (!window.google || !window.google.maps) return { formattedAddress: null };
    return new Promise((resolve) => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const formattedAddress = results[0].formatted_address || null;
          const components = ((results[0] as any)?.address_components || []) as any[];

          const getComponent = (type: string) =>
            (components.find((c: any) => c.types?.includes(type))?.long_name) ||
            undefined;

          resolve({
            formattedAddress,
            city: getComponent('locality') || getComponent('administrative_area_level_2'),
            state: getComponent('administrative_area_level_1'),
            zip: getComponent('postal_code'),
            country: getComponent('country'),
          });
        } else {
          resolve({ formattedAddress: null });
        }
      });
    });
  };

  const handleHospitalLocationChange = async (lat: number, lng: number, address?: string) => {
    const geo = await reverseGeocode(lat, lng);
    const resolvedAddress = address || geo.formattedAddress || formData.hospital.addressFormatted || '';
    setHospitalLocation({ latitude: lat, longitude: lng, address: resolvedAddress });
    setMapCenter({ lat, lng });
    setFormData((prev) => ({
      ...prev,
      hospital: {
        ...prev.hospital,
        latitude: lat,
        longitude: lng,
        addressFormatted: resolvedAddress,
        city: geo.city ?? prev.hospital.city,
        state: geo.state ?? prev.hospital.state,
        zipCode: geo.zip ?? prev.hospital.zipCode,
        country: geo.country ?? prev.hospital.country,
      },
    }));
    setValidationErrors((prev) => ({
      ...prev,
      hospitalLocation: '',
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setSubmitting(true);

    // Validate fields
    const nameError = validateName(formData.name);
    const codeError = validateCode(formData.code);
    let emailError = validateEmail(formData.email);
    let phoneError = validatePhone(formData.phone);
    const passwordError = validatePassword(formData.password);
    const hospitalEmailError = validateEmail(formData.hospital.email);
    const hospitalNameError = formData.hospital.name.trim() ? '' : 'Hospital name is required';
    const statusError = formData.status ? '' : 'Status is required';
    const hospitalLocationError =
      hospitalLocation && hospitalLocation.latitude && hospitalLocation.longitude
        ? ''
        : 'Hospital location is required';

    // Require at least one of email or phone
    if (!formData.email && !formData.phone) {
      const atLeastOneMessage = 'Please provide at least email or phone number';
      emailError = atLeastOneMessage;
      phoneError = atLeastOneMessage;
      setError(atLeastOneMessage);
    }

    setValidationErrors({
      name: nameError,
      code: codeError,
      email: emailError,
      phone: phoneError,
      password: passwordError,
      status: statusError,
      hospitalName: hospitalNameError,
      hospitalLocation: hospitalLocationError,
      hospitalEmail: hospitalEmailError,
    });

    if (
      nameError ||
      codeError ||
      emailError ||
      phoneError ||
      passwordError ||
      statusError ||
      hospitalNameError ||
      hospitalLocationError ||
      hospitalEmailError
    ) {
      setSubmitting(false);
      return;
    }

    try {
      // Build hospital object only if at least one field is filled
      const hospitalData: any = {};
      const hospitalDetails = (formData.hospital.addressDetails || '').trim();
      const hospitalGeoAddress = (formData.hospital.addressFormatted || '').trim();
      const hospitalAddressCombined = [hospitalDetails, hospitalGeoAddress].filter(Boolean).join(' | ');
      const hasHospitalFields =
        formData.hospital.name ||
        formData.hospital.logo ||
        hospitalAddressCombined ||
        formData.hospital.city ||
        formData.hospital.state ||
        formData.hospital.zipCode ||
        formData.hospital.country ||
        formData.hospital.phone ||
        formData.hospital.email ||
        formData.hospital.website ||
        formData.hospital.registrationNumber ||
        formData.hospital.licenseNumber ||
        formData.hospital.emergencyContact ||
        formData.hospital.emergencyPhone ||
        formData.hospital.latitude !== undefined ||
        formData.hospital.longitude !== undefined;

      if (hasHospitalFields) {
        if (formData.hospital.name) hospitalData.name = capitalizeName(formData.hospital.name);
        if (formData.hospital.logo) hospitalData.logo = formData.hospital.logo;
        if (hospitalAddressCombined) hospitalData.address = hospitalAddressCombined;
        if (formData.hospital.city) hospitalData.city = formData.hospital.city;
        if (formData.hospital.state) hospitalData.state = formData.hospital.state;
        if (formData.hospital.zipCode) hospitalData.zipCode = formData.hospital.zipCode;
        if (formData.hospital.country) hospitalData.country = formData.hospital.country;
        if (formData.hospital.phone) hospitalData.phone = formData.hospital.phone;
        if (formData.hospital.email) hospitalData.email = formData.hospital.email;
        if (formData.hospital.website) hospitalData.website = formData.hospital.website;
        if (formData.hospital.registrationNumber) hospitalData.registrationNumber = formData.hospital.registrationNumber;
        if (formData.hospital.licenseNumber) hospitalData.licenseNumber = formData.hospital.licenseNumber;
        if (formData.hospital.emergencyContact) hospitalData.emergencyContact = formData.hospital.emergencyContact;
        if (formData.hospital.emergencyPhone) hospitalData.emergencyPhone = formData.hospital.emergencyPhone;
        if (formData.hospital.latitude !== undefined) hospitalData.latitude = formData.hospital.latitude;
        if (formData.hospital.longitude !== undefined) hospitalData.longitude = formData.hospital.longitude;
      }

      const data = {
        metadata: { 
          name: capitalizeName(formData.name),
          code: formData.code.trim().toUpperCase(),
        },
        spec: {
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          description: formData.description,
          status: formData.status,
          ...((() => {
            const raw = formData.domain.trim();
            if (!raw) return editingTenant ? { domain: '' } : {};
            const normalized = raw.toLowerCase().replace(/^https?:\/\//i, '').split('/')[0].replace(/\/+$/, '').trim();
            return { domain: normalized };
          })()),
          ...(formData.groupId && { groupId: formData.groupId }),
          ...(formData.password && { password: formData.password }),
          ...(Object.keys(hospitalData).length > 0 && { hospital: hospitalData }),
        },
      };

      if (editingTenant) {
        await updateTenant(editingTenant._id, data);
      } else {
        await createTenant(data);
      }

      handleCloseModal();
      fetchTenants();
    } catch (err: any) {
      const message = err.response?.data?.message || 'Operation failed';
      setError(message);
      
      // Check if error is about code being taken
      const lowerMessage = message.toLowerCase();
      if (lowerMessage.includes('code') && (lowerMessage.includes('taken') || lowerMessage.includes('already'))) {
        setValidationErrors({
          ...validationErrors,
          code: 'This code is already taken',
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    setTenantToDelete({ id, name });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!tenantToDelete) return;

    try {
      await deleteTenant(tenantToDelete.id);
      fetchTenants();
      setShowDeleteConfirm(false);
      setTenantToDelete(null);
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Failed to delete tenant. Unknown error occurred.');
      setShowDeleteConfirm(false);
      setTenantToDelete(null);
      setShowErrorModal(true);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleRefresh = () => {
    fetchTenants();
  };

  const handlePageChange = (page: number) => {
    fetchTenants(page, pageSize);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    fetchTenants(1, size);
  };

  return (
    <DashboardLayout>
      <div className="page-header">
        <h2>Tenant Management</h2>
        <p>Manage all tenants in the system</p>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Tenants</h3>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end' }}>
            <input
              type="text"
              placeholder="Search by name, email or phone..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '16px',
                width: '220px',
                flex: '0 0 220px',
              }}
            />
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="refresh-button"
              title="Refresh list"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
            {hasPermission('tenant.create') && (
              <button className="btn-add" onClick={() => handleOpenModal()}>
                + Add Tenant
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading tenants...</div>
        ) : tenants.length === 0 ? (
          <div className="empty-state">
            <h3>No tenants yet</h3>
            <p>Create your first tenant to get started</p>
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
          <div className="table-container" style={{ height: 'calc(100vh - 320px)', overflowY: 'auto' }}>
            <table className="bookings-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Hospital</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((tenant) => (
                  <tr key={tenant._id}>
                    <td>{tenant.metadata.name}</td>
                    <td>{tenant.spec.email || '-'}</td>
                    <td>{tenant.spec.phone || '-'}</td>
                    <td>{tenant.spec.hospital?.name || '-'}</td>
                    <td>
                      <span className={`status-badge status-${tenant.spec.status}`}>
                        {tenant.spec.status}
                      </span>
                    </td>
                    <td>{formatDateDDMMYYYY(tenant.metadata.createdAt)}</td>
                    <td>
                      <div className="action-buttons">
                        {hasPermission('tenant.update') && (
                          <button className="btn-icon btn-edit" onClick={() => handleOpenModal(tenant)}>
                            Edit
                          </button>
                        )}
                        {isSystemAdmin && (
                          <button
                            className="btn-icon btn-edit"
                            onClick={() =>
                              setAdminLoginsTenant({ id: tenant._id, name: tenant.metadata.name })
                            }
                          >
                            Admin Logins
                          </button>
                        )}
                        {hasPermission('tenant.delete') && (
                          <button
                            className="btn-icon btn-delete"
                            onClick={() => handleDelete(tenant._id, tenant.metadata.name)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            <div className="pagination-controls" style={{ position: 'relative', width: '100%' }}>
              <div className="pagination-left" style={{ position: 'absolute', right: 250, whiteSpace: 'nowrap', zIndex: 10 }}>
                <label style={{ fontSize: '16px', whiteSpace: 'nowrap' }}>
                  Rows per page:{' '}
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    style={{ minWidth: '60px', width: 'auto' }}
                  >
                    {[10, 25, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="pagination-center">
                <span className="pagination-info">
                  {totalItems === 0
                    ? '0 of 0'
                    : `${(currentPage - 1) * pageSize + 1}–${Math.min(
                        currentPage * pageSize,
                        totalItems
                      )} of ${totalItems}`}
                </span>
              </div>
              <div className="pagination-right">
                <button
                  className="btn-secondary"
                  disabled={!hasPreviousPage || loading}
                  onClick={() => hasPreviousPage && handlePageChange(currentPage - 1)}
                >
                  ‹
                </button>
                <button
                  className="btn-secondary"
                  disabled={!hasNextPage || loading}
                  onClick={() => hasNextPage && handlePageChange(currentPage + 1)}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="drawer-overlay" onClick={handleCloseModal}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 className="drawer-title">
                {editingTenant ? 'Edit Tenant' : 'Create New Tenant'}
              </h3>
              <button className="drawer-close" onClick={handleCloseModal}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="drawer-form">
              <div className="drawer-body">
                {error && <div className="error-message">{error}</div>}

                <div className="form-group">
                  <label>
                    Tenant Name <span className="required-star">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    placeholder="Enter tenant name"
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({ ...formData, name: value });
                      setValidationErrors({
                        ...validationErrors,
                        name: validateName(value),
                      });
                    }}
                    className={validationErrors.name ? 'input-error' : ''}
                  />
                  {validationErrors.name && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.name}
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    Tenant Code <span className="required-star">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    placeholder="Enter tenant code (e.g., ABC123)"
                    onChange={(e) => {
                      // Only allow uppercase letters and numbers, convert to uppercase
                      const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                      setFormData({ ...formData, code: value });
                      setValidationErrors({
                        ...validationErrors,
                        code: validateCode(value),
                      });
                    }}
                    style={{ textTransform: 'uppercase' }}
                    className={validationErrors.code ? 'input-error' : ''}
                  />
                  {validationErrors.code && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.code}
                    </small>
                  )}
                  {!validationErrors.code && (
                    <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      Only uppercase letters and numbers allowed (e.g., ABC123)
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Domain</label>
                  <input
                    type="text"
                    value={formData.domain}
                    onChange={(e) => {
                      const raw = e.target.value.toLowerCase();
                      const noSpaces = raw.replace(/\s+/g, '');
                      setFormData({ ...formData, domain: noSpaces });
                    }}
                    onBlur={() => {
                      const raw = formData.domain.trim();
                      if (!raw) return;
                      const normalized = raw.toLowerCase().replace(/^https?:\/\//i, '').split('/')[0].replace(/\/+$/, '').trim();
                      if (normalized !== raw) setFormData({ ...formData, domain: normalized });
                    }}
                    placeholder="e.g. hospital.example.com or ambulance.binimise.com (hostname only; no https://)"
                    style={{ textTransform: 'lowercase' }}
                  />
                  <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                    Optional. Hostname only (e.g. ambulance.binimise.com). When set, users visiting this hostname may see the tenant&apos;s custom login UI.
                  </small>
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({ ...formData, email: value });
                      setValidationErrors({
                        ...validationErrors,
                        email: validateEmail(value),
                      });
                    }}
                    placeholder="tenant@example.com"
                    className={validationErrors.email ? 'input-error' : ''}
                  />
                  {validationErrors.email && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.email}
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => {
                      const numeric = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setFormData({ ...formData, phone: numeric });
                      setValidationErrors({
                        ...validationErrors,
                        phone: validatePhone(numeric),
                      });
                    }}
                    placeholder="1234567890"
                    className={validationErrors.phone ? 'input-error' : ''}
                    maxLength={10}
                  />
                  {validationErrors.phone && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.phone}
                    </small>
                  )}
                  {!validationErrors.phone && (
                  <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                    10 digits without spaces or special characters
                  </small>
                  )}
                </div>

                <div className="form-group">
                  <label>
                    Password
                    {!editingTenant && <span className="required-star">*</span>}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => {
                        setFormData({ ...formData, password: e.target.value });
                        setValidationErrors({ ...validationErrors, password: '' });
                      }}
                      placeholder={editingTenant ? 'Leave blank to keep current' : 'Minimum 8 characters'}
                      className={validationErrors.password ? 'input-error' : ''}
                      style={{ paddingRight: '45px' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '5px',
                        color: '#666',
                        fontSize: '20px',
                      }}
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  {validationErrors.password && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.password}
                    </small>
                  )}
                  {!editingTenant && !validationErrors.password && (
                    <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      Minimum 8 characters required
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Street, area, city (optional)"
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Short description about this tenant (optional)"
                  />
                </div>

                <div className="form-group">
                  <label>Group ID (GPS)</label>
                  <input
                    type="text"
                    value={formData.groupId}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        groupId: e.target.value.replace(/\D/g, ''),
                      })
                    }
                    placeholder="GPS group ID for all ambulances/vehicles"
                  />
                  <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                    All ambulances/vehicles under this tenant will use this group ID
                  </small>
                </div>

                <div className="form-group">
                  <label>
                    Status <span className="required-star">*</span>
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        status: e.target.value,
                      })
                    }
                    className={validationErrors.status ? 'input-error' : ''}
                  >
                    <option value="">Select status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                  {validationErrors.status && (
                    <small
                      style={{
                        color: '#e74c3c',
                        fontSize: '14px',
                        marginTop: '4px',
                        display: 'block',
                      }}
                    >
                      {validationErrors.status}
                    </small>
                  )}
                </div>

                {/* Hospital Details Section */}
                <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
                  <h4 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>Hospital Details</h4>
                  
                  <div className="form-group">
                    <label>
                      Hospital Name <span className="required-star">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.hospital.name}
                      onChange={(e) =>
                        setFormData({
                        ...formData, 
                          hospital: { ...formData.hospital, name: e.target.value },
                        })
                      }
                      placeholder="Hospital name"
                      className={validationErrors.hospitalName ? 'input-error' : ''}
                    />
                    {validationErrors.hospitalName && (
                      <small
                        style={{
                          color: '#e74c3c',
                          fontSize: '14px',
                          marginTop: '4px',
                          display: 'block',
                        }}
                      >
                        {validationErrors.hospitalName}
                      </small>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Logo</label>
                    <div className="avatar-upload-row">
                      <input
                        type="url"
                        value={formData.hospital.logo}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            hospital: { ...formData.hospital, logo: e.target.value },
                          })
                        }
                        placeholder="https://example.com/logo.png"
                        style={{ flex: '1 1 260px' }}
                      />
                      {!formData.hospital.logo && (
                        <label className={`avatar-upload-button ${logoUploading ? 'disabled' : ''}`} style={{ marginBottom: 0 }}>
                          <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleLogoFileChange}
                            disabled={logoUploading}
                          />
                          {logoUploading ? (
                            <>
                              <span className="loading-spinner btn-spinner" aria-hidden style={{ marginRight: '8px' }} />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <span role="img" aria-label="upload" style={{ marginRight: '8px', display: 'inline-block' }}>
                                📤
                              </span>
                              Upload
                            </>
                          )}
                        </label>
                      )}
                      {formData.hospital.logo && (
                        <button type="button" onClick={handleClearLogo} className="btn-secondary avatar-clear-button">
                          Clear
                        </button>
                      )}
                    </div>
                    {logoUploading && (
                      <small style={{ color: '#2563eb', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                        Processing & uploading logo...
                      </small>
                    )}
                    {logoUploadError && (
                      <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                        {logoUploadError}
                      </small>
                    )}
                    <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      Upload an image or paste a URL for the hospital logo. <span style={{ fontWeight: 600, color: '#374151' }}>Max size: 2MB.</span>
                    </small>
                    {formData.hospital.logo && (
                      <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div
                          style={{
                            width: 120,
                            borderRadius: 8,
                            overflow: 'hidden',
                            border: '1px solid #e5e7eb',
                            aspectRatio: '1 / 1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <img
                            src={formData.hospital.logo}
                            alt="Hospital logo preview"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                        </div>
                        <span className="avatar-preview-text">{formData.hospital.logo}</span>
                      </div>
                    )}
                  </div>

                <div className="form-group">
                  <label>Hospital Address Details</label>
                  <textarea
                    value={formData.hospital.addressDetails}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        hospital: { ...formData.hospital, addressDetails: e.target.value },
                      })
                    }
                    placeholder="Flat/area/landmark (combined with detected address)"
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                    }}
                  />
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>
                    Hospital Location <span className="required-star">*</span>
                  </label>
                  <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                    Search for a place or click on the map to set the hospital location
                  </p>
                  <PlaceSearch
                    onPlaceSelect={(lat, lng, address) => handleHospitalLocationChange(lat, lng, address)}
                    placeholder="Search for hospital location..."
                    currentLocation={hospitalLocation}
                  />
                  <GoogleMap
                    center={hospitalLocation ? { lat: hospitalLocation.latitude, lng: hospitalLocation.longitude } : mapCenter}
                    zoom={hospitalLocation ? 13 : 10}
                    location={
                      hospitalLocation
                        ? {
                            latitude: hospitalLocation.latitude,
                            longitude: hospitalLocation.longitude,
                          }
                        : null
                    }
                    onLocationChange={(lat, lng) => handleHospitalLocationChange(lat, lng)}
                    height="300px"
                    showMapTypeControl={true}
                  />
                  {hospitalLocation && (
                    <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                      <strong>Selected:</strong> {hospitalLocation.latitude.toFixed(6)}, {hospitalLocation.longitude.toFixed(6)}
                  {formData.hospital.addressFormatted && ` - ${formData.hospital.addressFormatted}`}
                    </div>
                  )}
                  {validationErrors.hospitalLocation && (
                    <small
                      style={{
                        color: '#e74c3c',
                        fontSize: '14px',
                        marginTop: '4px',
                        display: 'block',
                      }}
                    >
                      {validationErrors.hospitalLocation}
                    </small>
                  )}
                </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label>City</label>
                      <input
                        type="text"
                        value={formData.hospital.city}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          hospital: { ...formData.hospital, city: e.target.value } 
                        })}
                        placeholder="City"
                      />
                    </div>

                    <div className="form-group">
                      <label>State</label>
                      <input
                        type="text"
                        value={formData.hospital.state}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          hospital: { ...formData.hospital, state: e.target.value } 
                        })}
                        placeholder="State"
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label>Zip Code</label>
                      <input
                        type="text"
                        value={formData.hospital.zipCode}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          hospital: { ...formData.hospital, zipCode: e.target.value } 
                        })}
                        placeholder="Zip code"
                      />
                    </div>

                    <div className="form-group">
                      <label>Country</label>
                      <input
                        type="text"
                        value={formData.hospital.country}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          hospital: { ...formData.hospital, country: e.target.value } 
                        })}
                        placeholder="Country"
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label>Hospital Phone</label>
                      <input
                        type="tel"
                        value={formData.hospital.phone}
                        onChange={(e) =>
                          setFormData({
                          ...formData, 
                            hospital: {
                              ...formData.hospital,
                              phone: e.target.value.replace(/\D/g, '').slice(0, 10),
                            },
                          })
                        }
                        placeholder="Hospital phone"
                        maxLength={10}
                      />
                    </div>

                    <div className="form-group">
                      <label>Hospital Email</label>
                      <input
                        type="email"
                        value={formData.hospital.email}
                        onChange={(e) => {
                          setFormData({ 
                            ...formData, 
                            hospital: { ...formData.hospital, email: e.target.value } 
                          });
                          setValidationErrors({ ...validationErrors, hospitalEmail: '' });
                        }}
                        placeholder="hospital@example.com"
                        className={validationErrors.hospitalEmail ? 'input-error' : ''}
                      />
                      {validationErrors.hospitalEmail && (
                        <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                          {validationErrors.hospitalEmail}
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Website</label>
                    <input
                      type="url"
                      value={formData.hospital.website}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        hospital: { ...formData.hospital, website: e.target.value } 
                      })}
                      placeholder="https://example.com"
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label>Registration Number</label>
                      <input
                        type="text"
                        value={formData.hospital.registrationNumber}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          hospital: { ...formData.hospital, registrationNumber: e.target.value } 
                        })}
                        placeholder="Registration number"
                      />
                    </div>

                    <div className="form-group">
                      <label>License Number</label>
                      <input
                        type="text"
                        value={formData.hospital.licenseNumber}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          hospital: { ...formData.hospital, licenseNumber: e.target.value } 
                        })}
                        placeholder="License number"
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label>Emergency Contact</label>
                      <input
                        type="text"
                        value={formData.hospital.emergencyContact}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          hospital: { ...formData.hospital, emergencyContact: e.target.value } 
                        })}
                        placeholder="Emergency contact name"
                      />
                    </div>

                    <div className="form-group">
                      <label>Emergency Phone</label>
                      <input
                        type="tel"
                        value={formData.hospital.emergencyPhone}
                        onChange={(e) =>
                          setFormData({
                          ...formData, 
                            hospital: {
                              ...formData.hospital,
                              emergencyPhone: e.target.value.replace(/\D/g, '').slice(0, 10),
                            },
                          })
                        }
                        maxLength={10}
                        placeholder="Emergency phone"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="drawer-footer">
                <button type="button" className="btn-cancel" onClick={handleCloseModal} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting ? (
                    <>
                      <span className="loading-spinner btn-spinner" aria-hidden />
                      {editingTenant ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    `${editingTenant ? 'Update' : 'Create'} Tenant`
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Tenant"
        message={`Are you sure you want to delete the tenant "${tenantToDelete?.name}"? This action cannot be undone.`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setTenantToDelete(null);
        }}
      />

      <ConfirmModal
        isOpen={showErrorModal}
        title="Error"
        message={errorMessage}
        confirmText="OK"
        cancelText=""
        type="danger"
        onConfirm={() => setShowErrorModal(false)}
        onCancel={() => setShowErrorModal(false)}
      />

      {adminLoginsTenant && (
        <TenantAdminLoginsPanel
          tenantId={adminLoginsTenant.id}
          tenantName={adminLoginsTenant.name}
          onClose={() => setAdminLoginsTenant(null)}
        />
      )}
    </DashboardLayout>
  );
};

export default TenantManagement;

